// Display rules for the buyer's Order View payment block. They mirror the
// server (orderManagementRules.pending + orderPaymentMethodChange.availability);
// the server stays authoritative and answers with a code if they ever differ.
const DECISION_PENDING = ['not_started', 'authorized', 'requires_action', 'capture_failed'];
const CHANGEABLE = ['not_started', 'requires_action', 'capture_failed'];

export const isManagedLine = line => line?.orderManagement?.version === 1 && Boolean(line?.payment);

export const isAwaitingDecision = line => line?.status === 'Not processed'
  && (!line.sellerDecision?.status || line.sellerDecision.status === 'pending')
  && DECISION_PENDING.includes(line.payment?.status) && !line.payment?.lifecycle?.shippingBlocked;

// Who accepts the payment (Dropshipping In snapshot, else the Seller).
export const acceptanceActor = line => {
  const acceptance = line?.dropshippingIn?.paymentAcceptance;
  if (acceptance) return acceptance.acceptanceMode === 'provider' && acceptance.providerPermission === true ? 'provider' : 'seller';
  const providerId = line?.dropshippingIn?.providerId;
  return providerId && String(line?.orderManagement?.responsibleId || '') === String(providerId) ? 'provider' : 'seller';
};

// One stage per real payment state; null when the line predates managed
// orders (its payment state is not tracked the same way).
export const paymentStage = line => {
  if (!isManagedLine(line)) return null;
  const status = line.payment.status;
  if (status === 'captured') return 'captured';
  if (status === 'capturing' || status === 'capture_pending') return 'processing';
  if (status === 'canceled' || line.status === 'Cancelled') return 'canceled';
  if (status === 'requires_action') return 'bank_action';
  if (status === 'capture_failed') return 'failed';
  if (status === 'authorized') return 'authorized';
  if (status === 'not_started') {
    if (line.payment.provider === 'paypal') return 'paypal_approval';
    // Saved without a card: the Seller/Provider cannot accept it yet.
    return line.paymentIntent?.payment_method ? 'awaiting' : 'method_required';
  }
  return null;
};

// visible: show the action at all; allowed: it can be used right now.
export const paymentMethodChange = line => {
  if (!isManagedLine(line) || line.payment.provider === 'paypal' || !isAwaitingDecision(line)) return { visible: false, allowed: false };
  if (line.payment.status === 'authorized') return { visible: true, allowed: false, reason: 'errAuthorized' };
  return { visible: true, allowed: CHANGEABLE.includes(line.payment.status) };
};

export const isCardExpired = (card, now = new Date()) => {
  const year = Number(card?.exp_year), month = Number(card?.exp_month);
  return !year || !month || year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1);
};

const BRANDS = { visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', discover: 'Discover', jcb: 'JCB', diners: 'Diners Club', unionpay: 'UnionPay', cartes_bancaires: 'Cartes Bancaires' };
export const brandName = brand => BRANDS[String(brand || '').toLowerCase()] || (brand ? String(brand).charAt(0).toUpperCase() + String(brand).slice(1) : '');
export const shortCard = card => `${brandName(card?.brand)} •••• ${card?.last4 || ''}`.trim();
export const expiryText = card => card?.exp_month && card?.exp_year ? `${String(card.exp_month).padStart(2, '0')}/${card.exp_year}` : '';

// Server error code -> label key (orderManagementLabels.json).
export const errorKey = error => ({
  PAYMENT_METHOD_DECLINED: error?.reason === 'insufficient_funds' || error?.details?.declineCode === 'insufficient_funds' ? 'errInsufficient' : 'errDeclined',
  PAYMENT_METHOD_EXPIRED: 'errCardExpired',
  PAYMENT_METHOD_NOT_IN_WALLET: 'errNotInWallet', INVALID_PAYMENT_METHOD: 'errNotInWallet', BUYER_PAYMENT_REQUIRED: 'errNotInWallet',
  PAYMENT_ALREADY_AUTHORIZED: 'errAuthorized',
  PAYMENT_METHOD_LOCKED: 'errLocked', PAYMENT_METHOD_CHANGE_UNAVAILABLE: 'errLocked', PAYMENT_AUTHORIZATION_NOT_REQUIRED: 'errLocked',
  ORDER_BUSY: 'errBusy', ORDER_EXPIRED: 'errOrderExpired', ORDER_CHANGED: 'errChanged',
  PAYMENT_METHOD_UNAVAILABLE: 'errUnavailable',
}[error?.code] || 'errGeneric');
